import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type FilterFn,
  type Column,
  type Table,
} from '@tanstack/react-table'
import { SlidersHorizontal, Download, X, ChevronUp, ChevronDown, ChevronsUpDown, ListFilter } from 'lucide-react'

// ─── Module augmentation for column meta ─────────────────────────────────────

declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    label?: string       // CSV header label; defaults to column id
    skipExport?: boolean // exclude column from CSV (e.g. action columns)
  }
}

// ─── Filter value type ───────────────────────────────────────────────────────

interface ColFilterValue {
  text?: string
  nullCheck?: 'empty' | 'notEmpty'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const colFilterFn: FilterFn<any> = (row, columnId, value: ColFilterValue) => {
  const cell = row.getValue(columnId)
  const isEmpty = cell == null || cell === ''
  if (value.nullCheck === 'empty') return isEmpty
  if (value.nullCheck === 'notEmpty') return !isEmpty
  if (value.text) return String(cell ?? '').toLowerCase().includes(value.text.toLowerCase())
  return true
}
colFilterFn.autoRemove = (val: ColFilterValue) => !val?.text && !val?.nullCheck

// ─── CSV export ──────────────────────────────────────────────────────────────

function exportCsv<T>(table: Table<T>, filename: string) {
  const visibleCols = table.getVisibleLeafColumns().filter(
    (col) => !col.columnDef.meta?.skipExport && col.id !== '_select',
  )
  const headers = visibleCols.map((col) => col.columnDef.meta?.label ?? col.id)
  const rows = table.getFilteredRowModel().rows.map((row) =>
    visibleCols.map((col) => {
      const val = row.getValue(col.id)
      if (val == null) return ''
      const str = String(val)
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str
    }),
  )
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Column menu (sort + filter dropdown) ────────────────────────────────────

function ColumnMenu<T>({
  column,
  rect,
  onClose,
}: {
  column: Column<T, unknown>
  rect: DOMRect
  onClose: () => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)
  const currentFilter = column.getFilterValue() as ColFilterValue | undefined
  const [text, setText] = useState(currentFilter?.text ?? '')
  const [nullCheck, setNullCheck] = useState<'empty' | 'notEmpty' | undefined>(
    currentFilter?.nullCheck,
  )

  const applyFilter = useCallback(
    (nextText: string, nextNull: 'empty' | 'notEmpty' | undefined) => {
      const v: ColFilterValue = {}
      if (nextText) v.text = nextText
      if (nextNull) v.nullCheck = nextNull
      column.setFilterValue(Object.keys(v).length ? v : undefined)
    },
    [column],
  )

  function handleText(val: string) {
    setText(val)
    setNullCheck(undefined)
    applyFilter(val, undefined)
  }

  function handleNullCheck(val: 'empty' | 'notEmpty') {
    const next = nullCheck === val ? undefined : val
    setNullCheck(next)
    setText('')
    applyFilter('', next)
  }

  function clearFilter() {
    setText('')
    setNullCheck(undefined)
    column.setFilterValue(undefined)
    onClose()
  }

  const hasFilter = !!(text || nullCheck)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose()
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  const MENU_HEIGHT = 220
  const spaceBelow = window.innerHeight - rect.bottom
  const top = spaceBelow >= MENU_HEIGHT ? rect.bottom + 4 : rect.top - MENU_HEIGHT - 4
  const left = Math.max(4, Math.min(rect.left, window.innerWidth - 234))

  const sortBtnClass = (active: boolean) =>
    `flex items-center gap-2 w-full text-left px-2.5 py-1.5 rounded-lg text-[12px] cursor-pointer border-0 font-sans transition-colors ${
      active
        ? 'bg-primary text-on-primary font-semibold'
        : 'bg-transparent text-ink hover:bg-s2'
    }`

  return (
    <div
      ref={menuRef}
      style={{ position: 'fixed', top, left, zIndex: 9999 }}
      className="bg-surface border border-line shadow-xl rounded-xl p-2 w-[220px]"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {column.getCanSort() && (
        <>
          <button
            type="button"
            className={sortBtnClass(column.getIsSorted() === 'asc')}
            onClick={() => { column.toggleSorting(false); onClose() }}
          >
            <ChevronUp size={12} /> Sort A → Z
          </button>
          <button
            type="button"
            className={sortBtnClass(column.getIsSorted() === 'desc')}
            onClick={() => { column.toggleSorting(true); onClose() }}
          >
            <ChevronDown size={12} /> Sort Z → A
          </button>
          {column.getIsSorted() && (
            <button
              type="button"
              className={sortBtnClass(false)}
              onClick={() => { column.clearSorting(); onClose() }}
            >
              <ChevronsUpDown size={12} /> Clear sort
            </button>
          )}
          <div className="h-px bg-line-soft my-2" />
        </>
      )}

      {column.getCanFilter() && (
        <>
          <input
            autoFocus
            type="text"
            placeholder="Filter…"
            value={text}
            onChange={(e) => handleText(e.target.value)}
            className="w-full px-2.5 py-1.5 rounded-lg border border-line-soft bg-bg text-[12px] text-ink font-sans box-border mb-2"
          />

          <div className="flex flex-col gap-1 mb-1">
            {(
              [
                ['empty', 'Is empty'],
                ['notEmpty', 'Has value'],
              ] as const
            ).map(([val, label]) => (
              <label
                key={val}
                className="flex items-center gap-2 text-[12px] text-ink cursor-pointer px-1"
              >
                <input
                  type="checkbox"
                  checked={nullCheck === val}
                  onChange={() => handleNullCheck(val)}
                  className="cursor-pointer"
                />
                {label}
              </label>
            ))}
          </div>

          {hasFilter && (
            <button
              type="button"
              onClick={clearFilter}
              className="flex items-center gap-1 text-[11px] text-primary hover:underline px-1 mt-1 bg-transparent border-0 cursor-pointer font-sans"
            >
              <X size={10} /> Clear column filter
            </button>
          )}
        </>
      )}
    </div>
  )
}

// ─── Column visibility panel ─────────────────────────────────────────────────

function ColumnVisibilityPanel<T>({
  table,
  onClose,
}: {
  table: Table<T>
  onClose: () => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-1 z-50 bg-surface border border-line shadow-xl rounded-xl p-3 w-[180px]"
    >
      <div className="text-[11px] font-semibold text-ink-2 mb-2 uppercase tracking-wide">Columns</div>
      {table
        .getAllLeafColumns()
        .filter((col) => col.getCanHide())
        .map((col) => (
          <label key={col.id} className="flex items-center gap-2 text-[12px] text-ink cursor-pointer py-0.5">
            <input
              type="checkbox"
              checked={col.getIsVisible()}
              onChange={col.getToggleVisibilityHandler()}
            />
            {col.columnDef.meta?.label ?? col.id}
          </label>
        ))}
    </div>
  )
}

// ─── DataTable ────────────────────────────────────────────────────────────────

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[]
  data: TData[]
  /** Enable click-to-sort on column headers (default: true) */
  sortable?: boolean
  /** Enable per-column filter dropdown via header click */
  filterable?: boolean
  /** Enable column show/hide via gear icon */
  configurable?: boolean
  /** Enable CSV export button */
  exportable?: boolean
  exportFilename?: string
  pageSize?: number
  isLoading?: boolean
  emptyMessage?: string
  className?: string
  /** Renders on the left side of the DataTable toolbar. Forces toolbar to show. */
  toolbarSlot?: React.ReactNode
  /** Custom row key function (defaults to row index) */
  getRowId?: (row: TData, index: number) => string
  /** Called whenever the filtered (pre-pagination) row set changes. Use to scope bulk-select to visible rows. */
  onFilteredRowsChange?: (rows: TData[]) => void
}

export function DataTable<TData>({
  columns,
  data,
  sortable = true,
  filterable = false,
  configurable = false,
  exportable = false,
  exportFilename = 'export',
  pageSize = 50,
  isLoading = false,
  emptyMessage = 'No results.',
  className,
  toolbarSlot,
  getRowId,
  onFilteredRowsChange,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [openMenuCol, setOpenMenuCol] = useState<string | null>(null)
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null)
  const [showColPanel, setShowColPanel] = useState(false)

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters, columnVisibility },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
    defaultColumn: {
      filterFn: colFilterFn,
      enableSorting: sortable || filterable,
      enableColumnFilter: filterable,
      enableHiding: true,
    },
    ...(getRowId ? { getRowId } : {}),
  })

  useEffect(() => {
    onFilteredRowsChange?.(table.getFilteredRowModel().rows.map((r) => r.original))
  }, [columnFilters, data]) // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilterCount = columnFilters.length

  function openFilterMenu(colId: string, targetEl: HTMLElement) {
    const rect = targetEl.getBoundingClientRect()
    setMenuRect(rect)
    setOpenMenuCol((prev) => (prev === colId ? null : colId))
  }

  function handleHeaderClick(colId: string, canSort: boolean) {
    if (canSort && sortable) {
      table.getColumn(colId)?.toggleSorting()
    }
  }

  function clearAll() {
    table.resetColumnFilters()
    table.resetSorting()
  }

  const openColumn = openMenuCol ? table.getColumn(openMenuCol) : null

  const thBase =
    'px-3 py-2.5 text-left text-[11.5px] font-semibold text-ink-2 select-none whitespace-nowrap'
  const thInteractive =
    sortable || filterable ? 'cursor-pointer hover:bg-s2 transition-colors' : ''

  return (
    <div className={className}>
      {/* Toolbar */}
      {(filterable || configurable || exportable || toolbarSlot) && (
        <div className="flex items-center justify-between mb-3 gap-3">
          {/* Left slot + active filter indicator */}
          <div className="flex items-center gap-2 flex-wrap">
            {toolbarSlot}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="flex items-center gap-1.5 h-8 px-3 rounded-lg border-0 bg-amber-100 text-amber-800 text-[12px] font-semibold cursor-pointer font-sans hover:bg-amber-200 transition-colors"
              >
                <span>
                  {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
                </span>
                <X size={12} strokeWidth={2.5} />
                <span className="text-amber-600 font-normal">Clear all</span>
              </button>
            )}
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-2 relative">
            {configurable && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowColPanel((v) => !v)}
                  title="Show/hide columns"
                  className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-[12px] cursor-pointer font-sans transition-colors ${
                    showColPanel
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-line-soft bg-surface text-ink-2 hover:border-line'
                  }`}
                >
                  <SlidersHorizontal size={13} />
                  Columns
                </button>
                {showColPanel && (
                  <ColumnVisibilityPanel table={table} onClose={() => setShowColPanel(false)} />
                )}
              </div>
            )}

            {exportable && (
              <button
                type="button"
                onClick={() => exportCsv(table, exportFilename)}
                className="flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-line-soft bg-surface text-ink-2 text-[12px] cursor-pointer font-sans hover:border-line transition-colors"
              >
                <Download size={13} />
                Export CSV
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-surface rounded-[14px] border border-line-soft overflow-hidden">
        {isLoading ? (
          <div className="text-muted text-[14px] py-10 text-center">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id} className="border-b border-line-soft bg-s2">
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort()
                      const canFilter = header.column.getCanFilter()
                      const isSorted = header.column.getIsSorted()
                      const isFiltered = header.column.getIsFiltered()
                      const isMenuOpen = openMenuCol === header.column.id

                      return (
                        <th
                          key={header.id}
                          className={`${thBase} ${canSort && sortable ? thInteractive : ''} ${isMenuOpen ? 'bg-s2' : ''}`}
                          onClick={() => handleHeaderClick(header.column.id, canSort)}
                        >
                          <div className="flex items-center gap-1.5">
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                            {/* Sort indicator */}
                            {canSort && sortable && (
                              <span className="text-muted opacity-60 shrink-0">
                                {isSorted === 'asc' ? (
                                  <ChevronUp size={11} />
                                ) : isSorted === 'desc' ? (
                                  <ChevronDown size={11} />
                                ) : (
                                  <ChevronsUpDown size={11} className="opacity-40" />
                                )}
                              </span>
                            )}
                            {/* Filter icon — click opens per-column filter menu */}
                            {filterable && canFilter && (
                              <button
                                type="button"
                                title={isFiltered ? 'Filter active' : 'Filter'}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openFilterMenu(header.column.id, e.currentTarget)
                                }}
                                className={`ml-auto shrink-0 flex items-center gap-0.5 px-1 py-0.5 rounded border-0 cursor-pointer transition-colors ${
                                  isFiltered
                                    ? 'bg-amber-100 text-amber-700'
                                    : isMenuOpen
                                    ? 'bg-s2 text-ink'
                                    : 'bg-transparent text-muted hover:bg-s2'
                                }`}
                              >
                                <ListFilter size={10} strokeWidth={2.5} />
                                {isFiltered && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />}
                              </button>
                            )}
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-4 py-10 text-center text-muted text-[13px]"
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={i < table.getRowModel().rows.length - 1 ? 'border-b border-line-soft' : ''}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-3 py-3">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && table.getPageCount() > 1 && (
        <div className="flex items-center justify-between mt-4 gap-3">
          <div className="text-[12px] text-muted">
            {table.getFilteredRowModel().rows.length} row
            {table.getFilteredRowModel().rows.length !== 1 ? 's' : ''}
            {activeFilterCount > 0 ? ' (filtered)' : ''}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40"
            >
              ← Prev
            </button>
            <span className="text-[12px] text-muted">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <button
              type="button"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Column filter menu (rendered at fixed position) */}
      {openMenuCol && openColumn && menuRect && filterable && (
        <ColumnMenu
          column={openColumn as Column<TData, unknown>}
          rect={menuRect}
          onClose={() => setOpenMenuCol(null)}
        />
      )}
    </div>
  )
}
